import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { searchProfiles } from '../services/api';
import ArchetypeBadge from './ArchetypeBadge';
import { useAuth } from '../contexts/AuthContext';

const ARCHETYPES = [
  { id: '', name: 'All Archetypes' },
  // General
  { id: 'ANCHOR', name: 'The Anchor' },
  { id: 'CLUTCH', name: 'The Clutch Reactor' },
  { id: 'FRAGGER', name: 'The Fragger' },
  { id: 'STRATEGIST', name: 'The Strategist' },
  { id: 'RAW_TALENT', name: 'The Raw Talent' },
  { id: 'BALANCED', name: 'The Balanced Operator' },
  // FPS
  { id: 'AWPER', name: 'The AWPer (FPS)' },
  { id: 'ENTRY', name: 'The Entry Fragger (FPS)' },
  { id: 'RIFLER', name: 'The Rifler (FPS)' },
  { id: 'LURKER', name: 'The Lurker (FPS)' },
  { id: 'FPS_RAW_TALENT', name: 'FPS Raw Talent' },
  { id: 'FRAGMASTER', name: 'The Fragmaster (FPS)' },
  // MOBA
  { id: 'CARRY', name: 'The Carry / ADC (MOBA)' },
  { id: 'SUPPORT', name: 'The Support (MOBA)' },
  { id: 'JUNGLER', name: 'The Jungler (MOBA)' },
  { id: 'MID_ASSASSIN', name: 'The Mid / Assassin (MOBA)' },
  { id: 'MOBA_RAW_TALENT', name: 'MOBA Raw Talent' },
  { id: 'FLEX', name: 'The Flex (MOBA)' },
];

const GENRES = [
  { id: '', name: 'All Combines' },
  { id: 'general', name: 'General' },
  { id: 'fps', name: 'FPS' },
  { id: 'moba', name: 'MOBA' },
];

const REGIONS = [
  { id: '', name: 'All Regions' },
  { id: 'NA', name: 'NA' },
  { id: 'EU', name: 'EU' },
  { id: 'APAC', name: 'APAC' },
  { id: 'LATAM', name: 'LATAM' },
  { id: 'ME', name: 'ME' },
  { id: 'Other', name: 'Other' },
];

const ROLES = [
  { id: '', name: 'All Roles' },
  { id: 'Entry Fragger', name: 'Entry Fragger' },
  { id: 'Support', name: 'Support' },
  { id: 'IGL', name: 'IGL' },
  { id: 'Lurker', name: 'Lurker' },
  { id: 'AWPer / Sniper', name: 'AWPer / Sniper' },
  { id: 'Flex', name: 'Flex' },
  { id: 'Jungler', name: 'Jungler' },
  { id: 'Tank', name: 'Tank' },
  { id: 'Carry', name: 'Carry' },
  { id: 'Healer', name: 'Healer' },
];

const DIMENSION_LABELS = {
  rawSpeed:         'Speed',
  decisionQuality:  'Decision',
  aimPrecision:     'Aim',
  trackingAccuracy: 'Tracking',
  workingMemory:    'Memory',
  flickSpeed:       'Flick Spd',
  prioritization:   'Priority',
  consistency:      'Consistency',
};

const INITIAL_DIM_MINS = {
  rawSpeed: 0, decisionQuality: 0, aimPrecision: 0,
  trackingAccuracy: 0, workingMemory: 0, consistency: 0,
};

function ScoreCell({ value }) {
  const color = value >= 75 ? '#10b981' : value >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <td style={{ padding: '12px 8px', textAlign: 'center' }}>
      <span style={{ fontWeight: 700, color }}>{value}</span>
    </td>
  );
}

export default function ScoutDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const plan = user?.subscription?.plan || 'free';
  const isPro = plan === 'scout' || plan === 'team';
  const isTeam = plan === 'team';
  const compareLimit = isTeam ? 4 : 2;
  const [profiles, setProfiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    document.title = 'Scout Dashboard | NeuroRank';
    return () => { document.title = 'NeuroRank | Cognitive Combine'; };
  }, []);
  useEffect(() => { try { window.posthog?.capture('scout_dashboard_visited'); } catch {} }, []);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'grid'
  const PAGE_SIZE = 50;
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filters, setFilters] = useState({
    archetype: '', genre: '', region: '', role: '',
    minScore: 0, openToOpportunities: false,
  });
  const [dimMins, setDimMins] = useState(INITIAL_DIM_MINS);
  const [sortKey, setSortKey] = useState('overall');
  const [compareIds, setCompareIds] = useState([]);

  const toggleCompare = (id, e) => {
    e.stopPropagation();
    setCompareIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < compareLimit ? [...prev, id] : prev
    );
  };

  const exportCsv = () => {
    const DIMENSION_KEYS = Object.keys(DIMENSION_LABELS);
    const header = ['Player Tag', 'Profile ID', 'Genre', 'Archetype', 'Overall', ...DIMENSION_KEYS.map(k => DIMENSION_LABELS[k]), 'Region', 'Role', 'Open To Opps'];
    const rows = sorted.map((p) => {
      const overall = getOverall(p.scores);
      return [
        p.playerTag, p.shareableId, p.genre, p.archetype?.name || '',
        overall, ...DIMENSION_KEYS.map(k => p.scores[k] || 0),
        p.region || '', p.role || '', p.openToOpportunities ? 'Yes' : 'No',
      ];
    });
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `neurorank-scout-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const buildParams = useCallback((currentOffset = 0) => {
    const params = {
      archetype: filters.archetype || undefined,
      genre: filters.genre || undefined,
      region: filters.region || undefined,
      role: filters.role || undefined,
      minScore: filters.minScore || undefined,
      openToOpportunities: filters.openToOpportunities ? 'true' : undefined,
      limit: PAGE_SIZE,
      offset: currentOffset,
    };
    for (const [dim, val] of Object.entries(dimMins)) {
      if (val > 0) params[`dimMin_${dim}`] = val;
    }
    return params;
  }, [filters, dimMins, PAGE_SIZE]);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setOffset(0);
    try {
      const data = await searchProfiles(buildParams(0));
      setProfiles(data.profiles || []);
      setTotal(data.count || 0);
    } catch {
      setProfiles([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadMore = async () => {
    const nextOffset = offset + PAGE_SIZE;
    setLoadingMore(true);
    try {
      const data = await searchProfiles(buildParams(nextOffset));
      setProfiles((prev) => [...prev, ...(data.profiles || [])]);
      setOffset(nextOffset);
    } catch {
      // silently fail — existing results stay
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const getOverall = (scores) => {
    if (scores.overallScore !== undefined) return scores.overallScore;
    const vals = Object.entries(scores).filter(([k]) => k !== 'overallScore').map(([, v]) => v);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
  };

  const sorted = [...profiles].sort((a, b) => {
    if (sortKey === 'overall') return getOverall(b.scores) - getOverall(a.scores);
    return (b.scores[sortKey] || 0) - (a.scores[sortKey] || 0);
  });

  const activeDimFilters = Object.values(dimMins).filter((v) => v > 0).length;

  const SortHeader = ({ label, dimKey }) => (
    <th
      onClick={() => setSortKey(dimKey)}
      style={{
        padding: '10px 8px', textAlign: 'center', cursor: 'pointer',
        color: sortKey === dimKey ? 'var(--accent)' : 'var(--text-muted)',
        fontSize: 12, fontWeight: 600, letterSpacing: 1, userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {label} {sortKey === dimKey ? '↓' : ''}
    </th>
  );

  const SelectFilter = ({ label, value, options, field }) => (
    <div style={{ flex: 1, minWidth: 140 }}>
      <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
        {label}
      </label>
      <select
        className="input"
        value={value}
        onChange={(e) => setFilters((f) => ({ ...f, [field]: e.target.value }))}
        style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', width: '100%' }}
      >
        {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    </div>
  );

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid var(--border)', padding: '32px 0 24px' }}>
        <div className="container">
          <h1 style={{ marginBottom: 4 }}>Scout Dashboard</h1>
          <p className="text-muted">Search and filter neural profiles from the combine database.</p>
        </div>
      </div>

      <div className="container" style={{ paddingTop: 24 }}>
        {/* Filters */}
        <div className="card" style={{ marginBottom: 24 }}>
          {/* Basic filters row */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
            <SelectFilter label="ARCHETYPE" value={filters.archetype} field="archetype" options={ARCHETYPES} />
            <SelectFilter label="GENRE" value={filters.genre} field="genre" options={GENRES} />
            <SelectFilter label="REGION" value={filters.region} field="region" options={REGIONS} />
            <SelectFilter label="ROLE" value={filters.role} field="role" options={ROLES} />

            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
                MIN OVERALL: {filters.minScore || 'Any'}
              </label>
              <input
                type="range" min={0} max={90} step={5}
                value={filters.minScore}
                onChange={(e) => setFilters((f) => ({ ...f, minScore: parseInt(e.target.value) }))}
                style={{ width: '100%', accentColor: 'var(--accent)' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={filters.openToOpportunities}
                  onChange={(e) => setFilters((f) => ({ ...f, openToOpportunities: e.target.checked }))}
                  style={{ accentColor: 'var(--accent)', width: 14, height: 14 }}
                />
                Open to opportunities only
              </label>
              <button className="btn btn--primary" onClick={fetchProfiles} style={{ alignSelf: 'flex-start' }}>
                Search
              </button>
            </div>
          </div>

          {/* Advanced filters toggle */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              style={{
                background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                padding: '5px 14px', cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)',
              }}
            >
              {showAdvanced ? '▲ Hide' : '▼ Advanced'} Dimension Filters
              {activeDimFilters > 0 && (
                <span style={{
                  marginLeft: 8, background: 'var(--accent)', color: '#0a0e1a',
                  borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700,
                }}>
                  {activeDimFilters}
                </span>
              )}
            </button>

            {showAdvanced && (
              <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                {Object.entries(DIMENSION_LABELS).map(([key, label]) => (
                  <div key={key}>
                    <label style={{ fontSize: 12, color: dimMins[key] > 0 ? 'var(--accent)' : 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
                      MIN {label.toUpperCase()}: {dimMins[key] || 'Any'}
                    </label>
                    <input
                      type="range" min={0} max={90} step={5}
                      value={dimMins[key]}
                      onChange={(e) => setDimMins((d) => ({ ...d, [key]: parseInt(e.target.value) }))}
                      style={{ width: '100%', accentColor: 'var(--accent)' }}
                    />
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    style={{ fontSize: 12 }}
                    onClick={() => setDimMins(INITIAL_DIM_MINS)}
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Compare banner */}
        {compareIds.length > 0 && (
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--accent)', borderRadius: 10,
            padding: '12px 20px', marginBottom: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
              {compareIds.length < compareLimit ? `Select ${compareLimit - compareIds.length} more to compare` : `Ready to compare ${compareIds.length} players`}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn--ghost"
                style={{ fontSize: 12 }}
                onClick={() => setCompareIds([])}
              >
                Clear
              </button>
              {compareIds.length >= 2 && (
                <button
                  className="btn btn--primary"
                  style={{ fontSize: 12 }}
                  onClick={() => navigate(`/compare?a=${compareIds[0]}&b=${compareIds[1]}`)}
                >
                  Compare →
                </button>
              )}
            </div>
          </div>
        )}

        {/* Results */}
        <div className="card" style={{ padding: 0, overflow: 'hidden', position: 'relative', minHeight: isPro ? 0 : 340 }}>
          {/* Paywall overlay for non-Pro users */}
          {!isPro && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 10,
              backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
              background: 'rgba(6,9,15,0.75)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 14, borderRadius: 'inherit', textAlign: 'center', padding: '0 24px',
            }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: 'var(--text-primary)' }}>Scout Pro Required</h2>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 300, margin: 0, lineHeight: 1.6 }}>
                Unlock full search, advanced filters, profile compare, and CSV export.
              </p>
              <Link to="/pricing" className="btn btn--primary" style={{ fontSize: 13, padding: '10px 24px' }}>
                Upgrade — $14/mo
              </Link>
            </div>
          )}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              {loading ? 'Loading...' : `${profiles.length} of ${total} profiles`}
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {isTeam && sorted.length > 0 && (
                <button
                  className="btn btn--ghost"
                  onClick={exportCsv}
                  style={{ fontSize: 11, padding: '5px 12px' }}
                >
                  Export CSV
                </button>
              )}
              <span className="text-muted" style={{ fontSize: 12 }}>
                {viewMode === 'table' ? 'Click row to view · check to compare' : 'Click card to view'}
              </span>
              {/* View toggle */}
              <div style={{ display: 'flex', gap: 2, background: 'var(--bg-secondary)', borderRadius: 8, padding: 3, border: '1px solid var(--border)' }}>
                {[
                  { id: 'table', svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="4" rx="1" /><rect x="3" y="10" width="18" height="4" rx="1" /><rect x="3" y="17" width="18" height="4" rx="1" /></svg> },
                  { id: 'grid',  svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="8" height="8" rx="1.5" /><rect x="13" y="3" width="8" height="8" rx="1.5" /><rect x="3" y="13" width="8" height="8" rx="1.5" /><rect x="13" y="13" width="8" height="8" rx="1.5" /></svg> },
                ].map(({ id, svg }) => (
                  <button
                    key={id}
                    onClick={() => setViewMode(id)}
                    style={{
                      padding: '5px 8px', borderRadius: 6, border: 'none', cursor: 'pointer',
                      background: viewMode === id ? 'var(--accent)' : 'transparent',
                      color: viewMode === id ? '#000' : 'var(--text-muted)',
                      display: 'flex', alignItems: 'center',
                      transition: 'all 0.15s',
                    }}
                  >
                    {svg}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <p className="text-muted">Loading profiles...</p>
            </div>
          ) : sorted.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <p className="text-muted">No profiles match your filters.</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div style={{ padding: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {sorted.map((profile) => {
                const overall = getOverall(profile.scores);
                const genreColor = { general: '#00d4ff', fps: '#ef4444', moba: '#a855f7' }[profile.genre] || '#00d4ff';
                const topDim = Object.entries(profile.scores)
                  .filter(([k]) => k !== 'overallScore')
                  .sort(([, a], [, b]) => b - a)[0];
                return (
                  <div
                    key={profile.shareableId}
                    onClick={() => navigate(`/profile/${profile.shareableId}`)}
                    style={{
                      padding: '18px', borderRadius: 12, border: '1px solid var(--border)',
                      background: 'var(--bg-secondary)', cursor: 'pointer',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                  >
                    {/* Header row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>
                            {profile.playerTag}
                          </span>
                          {profile.openToOpportunities && (
                            <span style={{ fontSize: 9, fontWeight: 700, background: 'rgba(16,185,129,0.15)', color: '#10b981', borderRadius: 4, padding: '1px 5px' }}>OPEN</span>
                          )}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{profile.shareableId}</div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 22, fontWeight: 900, color: genreColor, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{overall}</div>
                        <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 1, textTransform: 'uppercase', letterSpacing: 1 }}>Overall</div>
                      </div>
                    </div>

                    {/* Archetype */}
                    <div style={{ marginBottom: 12 }}>
                      <ArchetypeBadge archetypeId={profile.archetype.id} archetypeName={profile.archetype.name} />
                    </div>

                    {/* Tags */}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: topDim ? 10 : 0 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10, border: `1px solid ${genreColor}44`, color: genreColor, textTransform: 'uppercase' }}>{profile.genre}</span>
                      {profile.region && <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10, border: '1px solid var(--border)', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{profile.region}</span>}
                      {profile.role && <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10, border: '1px solid var(--border)', color: 'var(--text-muted)' }}>{profile.role}</span>}
                    </div>

                    {/* Top dimension */}
                    {topDim && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          Top: {DIMENSION_LABELS[topDim[0]] || topDim[0]}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 800, color: '#10b981' }}>{topDim[1]}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ padding: '10px 8px', width: 32 }} />
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, letterSpacing: 1 }}>PLAYER</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, letterSpacing: 1 }}>ARCHETYPE</th>
                    <SortHeader label="OVERALL" dimKey="overall" />
                    {Object.entries(DIMENSION_LABELS).map(([key, label]) => (
                      <SortHeader key={key} label={label.toUpperCase()} dimKey={key} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((profile) => (
                    <tr
                      key={profile.shareableId}
                      onClick={() => navigate(`/profile/${profile.shareableId}`)}
                      style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '12px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={compareIds.includes(profile.shareableId)}
                          onChange={(e) => toggleCompare(profile.shareableId, e)}
                          disabled={!compareIds.includes(profile.shareableId) && compareIds.length >= compareLimit}
                          style={{ accentColor: 'var(--accent)', cursor: 'pointer', width: 14, height: 14 }}
                        />
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{profile.playerTag}</span>
                          {profile.openToOpportunities && (
                            <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(16,185,129,0.15)', color: '#10b981', borderRadius: 4, padding: '2px 6px' }}>
                              OPEN
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          {profile.game
                            ? <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{profile.game}{profile.rank ? ` · ${profile.rank}` : ''}</span>
                            : [profile.role, profile.region].filter(Boolean).join(' · ') || profile.shareableId
                          }
                          {profile.genre && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, letterSpacing: 1,
                              padding: '1px 6px', borderRadius: 4,
                              border: '1px solid var(--accent)', color: 'var(--accent)',
                              textTransform: 'uppercase',
                            }}>
                              {profile.genre}
                            </span>
                          )}
                        </div>
                        {/* Social link indicators */}
                        {profile.socialLinks && Object.values(profile.socialLinks).some(Boolean) && (
                          <div style={{ display: 'flex', gap: 4, marginTop: 5 }}>
                            {profile.socialLinks.twitter && (
                              <span title={`Twitter: @${profile.socialLinks.twitter}`} style={{ width: 16, height: 16, borderRadius: '50%', background: '#1d9bf022', border: '1px solid #1d9bf055', display: 'inline-block' }} />
                            )}
                            {profile.socialLinks.twitch && (
                              <span title={`Twitch: ${profile.socialLinks.twitch}`} style={{ width: 16, height: 16, borderRadius: '50%', background: '#9146ff22', border: '1px solid #9146ff55', display: 'inline-block' }} />
                            )}
                            {profile.socialLinks.youtube && (
                              <span title="YouTube" style={{ width: 16, height: 16, borderRadius: '50%', background: '#ff000022', border: '1px solid #ff000055', display: 'inline-block' }} />
                            )}
                            {profile.socialLinks.discord && (
                              <span title={`Discord: ${profile.socialLinks.discord}`} style={{ width: 16, height: 16, borderRadius: '50%', background: '#5865f222', border: '1px solid #5865f255', display: 'inline-block' }} />
                            )}
                            {profile.socialLinks.website && (
                              <span title="Website" style={{ width: 16, height: 16, borderRadius: '50%', background: 'var(--border)', border: '1px solid var(--text-muted)', display: 'inline-block' }} />
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <ArchetypeBadge archetypeId={profile.archetype.id} archetypeName={profile.archetype.name} />
                      </td>
                      <ScoreCell value={getOverall(profile.scores)} />
                      {Object.keys(DIMENSION_LABELS).map((key) => (
                        <ScoreCell key={key} value={profile.scores[key] || 0} />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Load More */}
        {!loading && profiles.length < total && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button
              className="btn btn--ghost"
              onClick={loadMore}
              disabled={loadingMore}
              style={{ minWidth: 160 }}
            >
              {loadingMore ? 'Loading...' : `Load More (${total - profiles.length} remaining)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
